// ---------------------------------------------------------------------------------------------
// Nombres, euros et pourcentages affichés à l'écran : seul endroit qui appelle Intl.NumberFormat
// ou toLocaleString('fr…') (règle oxlint gozoki/no-local-french-number-format dans le back-office).
//
// Règles communes :
// - virgule décimale, milliers séparés par une espace fine insécable (U+202F, celle d'Intl fr-FR),
//   une espace insécable (U+00A0) entre le nombre et son unité : « 1 234,50 € », « 3,5 % » ;
// - décimales fixes : « 12,0 % » et non « 12 % » quand on en demande une (sauf `formatDecimalMax`,
//   `formatPercentMax` et les graduations d'axe, `formatAxisTick`, qui n'écrivent pas de zéro
//   inutile) ;
// - arrondi au plus proche, la moitié s'éloignant de zéro (celui d'Intl) ;
// - jamais « -0 » : une valeur qui s'arrondit à zéro s'affiche sans signe ; `signed` ajoute « + »
//   devant une valeur positive non nulle ;
// - `null`, `undefined` et les valeurs non finies (`NaN`, `Infinity`) s'affichent `MISSING_VALUE`.
//
// Euros : `formatWholeEuros` à l'euro pour les agrégats d'une boutique ou du réseau (CA, marge,
// loyer, perte des dashboards) ; `formatEurosWithCents` au centime pour les montants d'un client,
// d'un panier ou d'un article (CA d'un client, panier moyen, prix, dette), qui se rapprochent de
// ses paniers au centime près.
// `formatCentsAsEuros` reste le format du texte brut (SMS, mail, journal) : espaces ordinaires et
// pas de séparateur de milliers, une espace insécable ferait passer un SMS en UCS-2.
// ---------------------------------------------------------------------------------------------

/** Espace insécable, entre un nombre et son unité (« 12 € », « 3,5 % »). */
export const NBSP = ' '

/** Valeur absente ou non finie, à la place d'un nombre. */
export const MISSING_VALUE = '—'

/** Valeur à afficher : `null` et `undefined` (donnée absente ou masquée) rendent `MISSING_VALUE`. */
export type DisplayedNumber = number | null | undefined

export interface SignOption {
  /** « + » devant une valeur positive non nulle (évolutions). */
  signed?: boolean
}

export interface PercentOptions extends SignOption {
  /** Décimales fixes, une par défaut. */
  decimals?: number
}

const numberFormats = new Map<string, Intl.NumberFormat>()

const numberFormat = (minDecimals: number, maxDecimals = minDecimals): Intl.NumberFormat => {
  const key = `${minDecimals}:${maxDecimals}`
  let format = numberFormats.get(key)
  if (format === undefined) {
    format = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
    })
    numberFormats.set(key, format)
  }
  return format
}

const signOf = (value: number, digits: string, signed: boolean): string => {
  // Le signe suit la valeur arrondie : -0,04 à une décimale s'affiche « 0,0 », pas « -0,0 ».
  if (!/[1-9]/.test(digits)) return ''
  if (value < 0) return '-'
  return signed ? '+' : ''
}

const formatWith = (
  format: Intl.NumberFormat,
  value: DisplayedNumber,
  unit: string,
  { signed = false }: SignOption = {},
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return MISSING_VALUE
  const digits = format.format(Math.abs(value))
  return `${signOf(value, digits, signed)}${digits}${unit}`
}

const formatWithUnit = (
  value: DisplayedNumber,
  decimals: number,
  unit: string,
  options?: SignOption,
): string => formatWith(numberFormat(decimals), value, unit, options)

/** Changement d'unité d'une valeur présente, `null` et `undefined` conservés. */
const convert = (value: DisplayedNumber, toUnit: (value: number) => number): DisplayedNumber =>
  value === null || value === undefined ? value : toUnit(value)

const centsToEuros = (cents: number): number => cents / 100
const eurosToKiloEuros = (euros: number): number => euros / 1000
const ratioToPercent = (ratio: number): number => ratio * 100

/** Nombre arrondi à l'unité : « 1 235 ». */
export const formatInteger = (value: DisplayedNumber, options?: SignOption): string =>
  formatWithUnit(value, 0, '', options)

/** Nombre à `decimals` décimales fixes : `formatDecimal(1234.5, 1)` → « 1 234,5 ». */
export const formatDecimal = (
  value: DisplayedNumber,
  decimals: number,
  options?: SignOption,
): string => formatWithUnit(value, decimals, '', options)

/**
 * Nombre à au plus `maxDecimals` décimales, sans zéro inutile : `formatDecimalMax(1.5, 2)` →
 * « 1,5 », `formatDecimalMax(1.0904, 3)` → « 1,09 », `formatDecimalMax(100, 3)` → « 100 ». Pour une
 * valeur affichée dont la précision suit la donnée (facteur, coefficient, part saisie) ; à décimales
 * fixes, `formatDecimal`, et sur un axe de graphique, `formatAxisTick`.
 */
export const formatDecimalMax = (
  value: DisplayedNumber,
  maxDecimals: number,
  options?: SignOption,
): string => formatWith(numberFormat(0, maxDecimals), value, '', options)

/** Euros arrondis à l'euro, agrégats d'une boutique ou du réseau : « 1 235 € ». */
export const formatWholeEuros = (euros: DisplayedNumber, options?: SignOption): string =>
  formatWithUnit(euros, 0, `${NBSP}€`, options)

/** Euros au centime, montants d'un client, d'un panier ou d'un article : « 1 234,50 € ». */
export const formatEurosWithCents = (euros: DisplayedNumber, options?: SignOption): string =>
  formatWithUnit(euros, 2, `${NBSP}€`, options)

/** Centimes arrondis à l'euro (prévisions, montants stockés en centimes) : 123_450 → « 1 235 € ». */
export const formatCentsAsWholeEuros = (cents: DisplayedNumber, options?: SignOption): string =>
  formatWholeEuros(convert(cents, centsToEuros), options)

/** Centimes au centime (prix, dette, panier stockés en centimes) : 123_450 → « 1 234,50 € ». */
export const formatCentsAsEurosWithCents = (cents: DisplayedNumber, options?: SignOption): string =>
  formatEurosWithCents(convert(cents, centsToEuros), options)

/** Milliers d'euros arrondis, axes et vues compactes : 12_400 → « 12 k€ ». */
export const formatKiloEuros = (euros: DisplayedNumber, options?: SignOption): string =>
  formatWithUnit(convert(euros, eurosToKiloEuros), 0, `${NBSP}k€`, options)

/**
 * Pourcentage déjà exprimé en points (12.34 → « 12,3 % ») ; une part entre 0 et 1 passe par
 * `formatRatioAsPercent`. Une décimale par défaut.
 */
export const formatPercent = (
  percent: DisplayedNumber,
  { decimals = 1, signed = false }: PercentOptions = {},
): string => formatWithUnit(percent, decimals, `${NBSP}%`, { signed })

/** Part entre 0 et 1 en pourcentage : 0.038 → « 3,8 % » (mêmes options que `formatPercent`). */
export const formatRatioAsPercent = (ratio: DisplayedNumber, options?: PercentOptions): string =>
  formatPercent(convert(ratio, ratioToPercent), options)

/**
 * Pourcentage déjà en points à au plus `maxDecimals` décimales, sans zéro inutile, comme
 * `formatDecimalMax` : `formatPercentMax(100 / 3, 3)` → « 33,333 % », `formatPercentMax(40, 1)` →
 * « 40 % ». Pour une part dont la précision suit la donnée (réglage, part saisie) ; à décimales
 * fixes, `formatPercent`.
 */
export const formatPercentMax = (
  percent: DisplayedNumber,
  maxDecimals: number,
  options?: SignOption,
): string => formatWith(numberFormat(0, maxDecimals), percent, `${NBSP}%`, options)

/**
 * Graduation d'axe de graphique : au plus `maxDecimals` décimales, sans zéro inutile (« 15 »,
 * « 2,5 »), et l'unité éventuelle après une espace insécable (`formatAxisTick(10, 0, '%')` →
 * « 10 % »). Une valeur affichée passe par `formatDecimal` ou `formatPercent` (décimales fixes),
 * ou par `formatDecimalMax` (au plus N décimales).
 */
export const formatAxisTick = (value: DisplayedNumber, maxDecimals = 0, unit = ''): string =>
  formatWith(numberFormat(0, maxDecimals), value, unit === '' ? '' : `${NBSP}${unit}`)

/**
 * Montant en centimes → `12,50` : deux décimales, virgule française, sans séparateur de milliers
 * ni symbole (relisible tel quel par un champ de saisie, l'unité s'affiche à part).
 */
export const formatCentsAsDecimal = (cents: number): string =>
  (cents / 100).toFixed(2).replace('.', ',')

/**
 * Montant en centimes → `12,50 €` pour du texte brut (SMS, mail, journal d'audit) : deux décimales,
 * virgule, espaces ordinaires et sans séparateur de milliers. À l'écran : `formatEurosWithCents`.
 */
export const formatCentsAsEuros = (cents: number): string => `${formatCentsAsDecimal(cents)} €`

/** Durée en secondes → `3 h 05`, ou `45 min` sous l'heure. */
export const formatSecondsAsHoursMinutes = (seconds: number): string => {
  // Arrondir le total d'abord : arrondir le reste après extraction des heures donnerait `1 h 60`.
  const totalMinutes = Math.round(seconds / 60)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return hours > 0 ? `${hours} h ${String(minutes).padStart(2, '0')}` : `${minutes} min`
}

/**
 * Adresse sur une ligne, « 12 rue de la Benauge, 33100 Bordeaux » : libellé de la liste des hubs et
 * en-tête de la fiche hub (back-office), requête de géocodage des profils boutique et e-mail de création de boutique. Les parties vides
 * sont omises ; `null` si tout est vide. D'autres lignes d'adresse sont encore construites à la
 * main (panier, démographie) : les faire passer ici quand on y touche.
 */
export const formatAddressLine = (address: {
  address: string
  postalCode: string
  city: string
}): string | null => {
  const locality = [address.postalCode, address.city]
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .join(' ')
  const parts = [address.address.trim(), locality].filter((part) => part !== '')
  return parts.length > 0 ? parts.join(', ') : null
}
